import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { useConfirm } from 'material-ui-confirm';

import {
  Cancel as CancelIcon,
  ContentCopyOutlined as ContentCopyOutlinedIcon,
  DeleteOutlineOutlined as DeleteOutlineOutlinedIcon,
  EditOutlined as EditOutlinedIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';

import { Dialog, DialogActions, DialogContent } from 'material/components/Dialog';
import Button from 'material/components/Button';

import {
  deleteTextBroadcast,
  fetchTextBroadcast,
  stopTextBroadcast,
} from 'store/actions/broadcasts';
import { broadcastsSelectors } from 'store/selectors/broadcasts';
import { setSnackbarError, setSnackbarSuccess, setSnackbarWarning } from 'store/actions/global';

import { PhoneView } from 'common/components';

import { cancellationStatuses, statuses } from 'utils/constants';

import TopCard from 'common/containers/TopCard';

const chipColor = (value, sendAtUtc) => {
  const { draft, published } = statuses;

  switch (value) {
    case published: {
      const sent = new Date().getTime() - new Date(sendAtUtc).getTime() > 0;

      return sent ? 'secondary' : 'blue';
    }
    case draft:
      return 'grey';
    default:
      return null;
  }
};

const chipLabel = (value, sendAtUtc) => {
  const { draft, published } = statuses;

  switch (value) {
    case published: {
      const sent = new Date().getTime() - new Date(sendAtUtc).getTime() > 0;

      return sent ? 'Sent' : 'Scheduled';
    }
    case draft:
      return 'Draft';
    default:
      return null;
  }
};

const TextBroadcastTopCard = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const confirm = useConfirm();
  const { draft } = statuses;
  const { probablyCancelled } = cancellationStatuses;
  const { broadcastUuid, organizationUuid } = useParams();

  const { textBroadcastDetails } = useSelector(state => ({
    textBroadcastDetails: broadcastsSelectors.getTextBroadcastDetails(state),
  }));

  const { contactLists, createdBy, isCancellable, message, name, sendAt, sendAtUtc, status } =
    textBroadcastDetails;

  const [isMessageOpen, setMessageOpen] = useState(false);

  const handleMessageClose = () => setMessageOpen(false);

  const handleEditBroadcast = () => {
    history.push(`/${organizationUuid}/${broadcastUuid}/create-text-broadcast`);
  };

  const handleDuplicateBroadcast = () => {
    history.push(`/${organizationUuid}/create-text-broadcast/${broadcastUuid}`);
  };

  const handleDeleteBroadcast = () => {
    confirm({
      confirmationText: 'Delete',
      description: 'Deleting this broadcast will also remove any related recipient analytics.',
      title: 'Delete the Broadcast?',
    })
      .then(() => dispatch(deleteTextBroadcast(broadcastUuid)))
      .then(() => history.push(`/${organizationUuid}/broadcasts`));
  };

  const handleStopBroadcast = () => {
    dispatch(stopTextBroadcast(broadcastUuid))
      .then(({ message: stopMessage, status: stoppedBroadcastStatus }) => {
        if (stoppedBroadcastStatus === probablyCancelled) {
          dispatch(setSnackbarWarning({ message: stopMessage }));
        } else {
          dispatch(setSnackbarSuccess({ message: stopMessage }));
        }
        dispatch(fetchTextBroadcast(broadcastUuid));
      })
      .catch(({ message: stopMessage }) => dispatch(setSnackbarError({ message: stopMessage })));
  };

  const handleConfirmStopBroadcast = () => {
    confirm({
      cancellationText: 'No',
      confirmationText: 'Yes, Cancel',
      description: '',
      title: 'Cancel send the Broadcast?',
    }).then(() => handleStopBroadcast(broadcastUuid));
  };

  const topCardData = useCallback(() => {
    const chipData = {
      chipColor: chipColor(status, sendAtUtc),
      chipLabel: chipLabel(status, sendAtUtc),
      chipOptions: [],
      chipOptionsHeader: 'Change status',
    };

    const actionButtons = [
      {
        color: 'grey',
        disabled: status !== draft,
        name: 'Edit',
        onClick: handleEditBroadcast,
        startIcon: <EditOutlinedIcon />,
      },
      {
        color: 'grey',
        name: 'Duplicate',
        onClick: handleDuplicateBroadcast,
        startIcon: <ContentCopyOutlinedIcon />,
      },
      {
        color: 'grey',
        name: 'Delete',
        onClick: handleDeleteBroadcast,
        startIcon: <DeleteOutlineOutlinedIcon />,
      },
    ];

    if (isCancellable) {
      actionButtons.unshift({
        color: 'grey',
        disabled: false,
        name: 'Cancel send',
        onClick: handleConfirmStopBroadcast,
        startIcon: <CancelIcon />,
      });
    }

    const additionalActionButtons = [
      {
        color: 'primary',
        endIcon: <OpenInNewIcon />,
        name: 'Preview message',
        onClick: () => setMessageOpen(true),
      },
    ];

    const details = [
      ...(sendAt
        ? [
            {
              label: 'Scheduled at',
              value: new Date(sendAt).toLocaleString('us-US'),
            },
          ]
        : []),
      {
        label: 'Created by',
        value: `${createdBy?.firstName} ${createdBy?.lastName}`,
      },
      {
        label: 'Lists',
        value: contactLists?.map(({ name: listName }) => listName).join(', '),
      },
    ];

    return {
      actionButtons,
      additionalActionButtons,
      backButtonLink: `/${organizationUuid}/broadcasts`,
      chipData,
      details,
      entityTypes: ['Broadcast', 'Text Message'],
      title: name || '',
    };
  }, [name, status, draft, isCancellable, probablyCancelled, organizationUuid]);

  return (
    <>
      <TopCard {...topCardData()} />
      <Dialog
        aria-labelledby="max-width-dialog-message"
        maxWidth="lg"
        open={isMessageOpen}
        onClose={handleMessageClose}
      >
        <DialogContent>
          <PhoneView text={message} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleMessageClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TextBroadcastTopCard;
